import type { BuyerRow } from "../data/types";

/**
 * Schedule helpers — turn the flat buyers slice into venue-grouped
 * meet entries sorted by time. Used by both the desktop Schedule cube
 * and the mobile Tonight tab so behavior stays in sync.
 *
 * Venue normalization: agreed_meet_spot is a free-text string. We
 * bucket into "Cosmo" / "Festival" / "Other" by case-insensitive
 * substring match — matches the two venues Yury actually uses
 * (see template chips "Avail · Cosmo" / "Avail · festival" in 3.A).
 */

export type Venue = "Cosmo" | "Festival" | "Other";

export interface ScheduleEntry {
  buyer:               BuyerRow;
  meetAt:              number;
  venue:               Venue;
  rawSpot:             string;
  /** Set to the OTHER entry's buyer.display_name when within CONFLICT_MIN. */
  conflictsWith:       string | null;
}

const CONFLICT_MIN = 5;

export function buildSchedule(buyers: BuyerRow[]): Record<Venue, ScheduleEntry[]> {
  const grouped: Record<Venue, ScheduleEntry[]> = {
    Cosmo: [], Festival: [], Other: [],
  };
  for (const b of buyers) {
    if (b.conv_agreed_meet_at == null) continue;
    if (b.conv_status === "ghosted" || b.conv_status === "declined") continue;
    const venue = classifyVenue(b.conv_agreed_meet_spot);
    grouped[venue].push({
      buyer:         b,
      meetAt:        b.conv_agreed_meet_at,
      venue,
      rawSpot:       b.conv_agreed_meet_spot ?? "",
      conflictsWith: null,
    });
  }
  for (const v of Object.keys(grouped) as Venue[]) {
    grouped[v].sort((a, b) => a.meetAt - b.meetAt);
    // Flag conflicts: for each entry, find any OTHER entry in the
    // same venue within CONFLICT_MIN. Since entries are sorted by
    // time, we only need to compare each to its neighbors.
    const list = grouped[v];
    for (let i = 0; i < list.length; i++) {
      const cur = list[i]!;
      const prev = i > 0 ? list[i - 1]! : null;
      const next = i < list.length - 1 ? list[i + 1]! : null;
      if (prev && isConflict(cur.meetAt, prev.meetAt, CONFLICT_MIN)) {
        cur.conflictsWith = prev.buyer.display_name;
      } else if (next && isConflict(cur.meetAt, next.meetAt, CONFLICT_MIN)) {
        cur.conflictsWith = next.buyer.display_name;
      }
    }
  }
  return grouped;
}

/** Detect a conflict for a candidate (venue, time) against the current
 *  buyers slice. Returns the conflicting buyer's display_name, or null.
 *  Used by the editor's inline warning. */
export function findConflictFor(
  buyers:   BuyerRow[],
  venue:    Venue,
  meetAt:   number,
  selfConvId: string | null,
): string | null {
  for (const b of buyers) {
    if (b.conv_id === selfConvId) continue;
    if (b.conv_agreed_meet_at == null) continue;
    if (b.conv_status === "ghosted" || b.conv_status === "declined") continue;
    const otherVenue = classifyVenue(b.conv_agreed_meet_spot);
    if (otherVenue !== venue) continue;
    if (isConflict(meetAt, b.conv_agreed_meet_at, CONFLICT_MIN)) {
      return b.display_name;
    }
  }
  return null;
}

export function classifyVenue(spot: string | null): Venue {
  if (!spot) return "Other";
  const s = spot.toLowerCase();
  if (s.includes("cosmo"))    return "Cosmo";
  if (s.includes("festival")) return "Festival";
  return "Other";
}

/** "8:30 PM" — local time, 12-hour, no seconds. */
export function formatTime(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** "Sun, May 18 · 8:30 PM" — full label for hover/detail. */
export function formatDateTime(ms: number): string {
  const d = new Date(ms);
  return `${d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })} · ${formatTime(ms)}`;
}

/** True if two timestamps are within `minutes` of each other. */
export function isConflict(a: number, b: number, minutes = 5): boolean {
  return Math.abs(a - b) < minutes * 60 * 1000;
}
